import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import {
  getCompanies,
  getCompany,
  createCompany,
  updateCompany,
  deleteCompany,
  getContacts,
  getAllContacts,
  createContact,
  updateContact,
  deleteContact,
  sendPartnerInvite,
} from '@/services/companies';
import { useAuth } from '@/contexts/AuthContext';
import type { CreateCompanyInput, CreateContactInput } from '@/types';

// ===========================================
// Query Keys
// ===========================================

export const companyKeys = {
  all: ['companies'] as const,
  lists: () => [...companyKeys.all, 'list'] as const,
  list: (filters: Record<string, unknown>) => [...companyKeys.lists(), filters] as const,
  details: () => [...companyKeys.all, 'detail'] as const,
  detail: (id: string) => [...companyKeys.details(), id] as const,
  contacts: (companyId: string) => [...companyKeys.detail(companyId), 'contacts'] as const,
  allContacts: () => [...companyKeys.all, 'contacts', 'all'] as const,
};

// ===========================================
// Companies Hooks
// ===========================================

/**
 * Fetch all companies
 */
export function useCompanies() {
  return useQuery({
    queryKey: companyKeys.lists(),
    queryFn: getCompanies,
  });
}

/**
 * Fetch a single company
 */
export function useCompany(id: string | undefined) {
  return useQuery({
    queryKey: companyKeys.detail(id!),
    queryFn: () => getCompany(id!),
    enabled: !!id,
  });
}

/**
 * Create a new company
 */
export function useCreateCompany() {
  const queryClient = useQueryClient();
  const { user } = useAuth();

  return useMutation({
    mutationFn: (input: CreateCompanyInput) => {
      if (!user) throw new Error('User not authenticated');
      return createCompany(input, user.id);
    },
    onSuccess: (company) => {
      queryClient.invalidateQueries({ queryKey: companyKeys.lists() });
      toast.success(`${company.name} created successfully`);
    },
    onError: (error) => {
      console.error('Create company error:', error);
      toast.error('Failed to create company');
    },
  });
}

/**
 * Update an existing company
 */
export function useUpdateCompany() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, input }: { id: string; input: Partial<CreateCompanyInput> }) =>
      updateCompany(id, input),
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: companyKeys.lists() });
      queryClient.invalidateQueries({ queryKey: companyKeys.detail(id) });
      toast.success('Company updated successfully');
    },
    onError: (error) => {
      console.error('Update company error:', error);
      toast.error('Failed to update company');
    },
  });
}

/**
 * Delete a company
 */
export function useDeleteCompany() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: deleteCompany,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: companyKeys.lists() });
      toast.success('Company deleted successfully');
    },
    onError: (error) => {
      console.error('Delete company error:', error);
      toast.error('Failed to delete company');
    },
  });
}

// ===========================================
// Contacts Hooks
// ===========================================

/**
 * Fetch every contact across every company (collection group read).
 * Used to power cross-company search on the Referral Sources page.
 */
export function useAllContacts() {
  return useQuery({
    queryKey: companyKeys.allContacts(),
    queryFn: getAllContacts,
  });
}

/**
 * Fetch contacts for a company
 */
export function useContacts(companyId: string | undefined) {
  return useQuery({
    queryKey: companyKeys.contacts(companyId!),
    queryFn: () => getContacts(companyId!),
    enabled: !!companyId,
  });
}

/**
 * Create a new contact
 */
export function useCreateContact(companyId: string) {
  const queryClient = useQueryClient();
  const { user } = useAuth();

  return useMutation({
    mutationFn: (input: CreateContactInput) => {
      if (!user) throw new Error('User not authenticated');
      return createContact(companyId, input, user.id);
    },
    onSuccess: (contact) => {
      queryClient.invalidateQueries({ queryKey: companyKeys.contacts(companyId) });
      toast.success(`${contact.firstName} ${contact.lastName} added`);
    },
    onError: (error) => {
      console.error('Create contact error:', error);
      toast.error('Failed to add contact');
    },
  });
}

/**
 * Update a contact
 */
export function useUpdateContact(companyId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ contactId, input }: { contactId: string; input: Partial<CreateContactInput> }) =>
      updateContact(companyId, contactId, input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: companyKeys.contacts(companyId) });
      toast.success('Contact updated');
    },
    onError: (error) => {
      console.error('Update contact error:', error);
      toast.error('Failed to update contact');
    },
  });
}

/**
 * Delete a contact
 */
export function useDeleteContact(companyId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (contactId: string) => deleteContact(companyId, contactId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: companyKeys.contacts(companyId) });
      toast.success('Contact deleted');
    },
    onError: (error) => {
      console.error('Delete contact error:', error);
      toast.error('Failed to delete contact');
    },
  });
}

// ===========================================
// Partner Invite Hook
// ===========================================

/**
 * Send partner portal invite email
 */
export function useSendPartnerInvite() {
  return useMutation({
    mutationFn: (input: {
      companyId: string;
      companyName: string;
      contactEmail: string;
      contactName: string;
    }) => sendPartnerInvite(input),
    onSuccess: () => {
      toast.success('Partner invitation email sent successfully');
    },
    onError: (error: any) => {
      console.error('Send partner invite error:', error);
      const message = error?.message || 'Failed to send partner invitation';
      toast.error(message);
    },
  });
}
